import { Injectable, OnDestroy } from '@angular/core';
import { enumReviewStatus, IReviewList } from '../models/review';
import { UserService } from './user.service';
import { Subject } from '../models/subject';
import { SubjectReview } from '../models/subject';
import { Review } from '../models/review';
import { Assessment } from '../models/assessment';
import { SubjectAssessment } from '../models/subject';
import { Observable } from 'rxjs';
import { BaseRestService } from './base-rest.service';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { map, switchMap, take } from 'rxjs/operators';
import { SubjectTypeService } from './subject-type.service';
import { SubjectService } from './subject.service';
import { EventService } from './event.service';
import { AutoUnsubscribe } from 'auto-unsubscribe.decorator';

@AutoUnsubscribe()
@Injectable({ providedIn: 'root' })
export class ReviewService extends BaseRestService<Review, IReviewList> implements OnDestroy{

  constructor(
    http: HttpClient,
    private userService: UserService,
    private subjectService: SubjectService,
    private subjectTypeService: SubjectTypeService,
    private eventService: EventService,
    router: Router) {
    super(ReviewService, http, 'api/reviews', 'review', router);
  }

  ngOnDestroy(): void { }

  addReview(subject: Subject, assessment: Assessment, status: enumReviewStatus, label?: string,
    comments?: string, override?: boolean, overrideReason?: string): Observable<Subject> {
    // create and populate review...
    const review = new Review();
    review.status = status;
    review.reviewedById = this.userService.getCurrentUserId();
    if (label) {
      review.label = label;
    }
    if (comments) {
      review.comments = comments;
    }
    if (override) {
      review.override = override;
      review.overrideReason = overrideReason;
    }

    // start by saving the review - we need this so we only include the review once in the database
    return this.saveAsObservable(review).pipe(take(1), switchMap((response: Review) => {

      // ...link to subject
      subject.subjectReviews = subject.subjectReviews || [];
      subject.subjectReviews.push(new SubjectReview({ reviewId: response.id, review: response }));

      // stash reviewedBy
      const reviewedBy = response.reviewedBy;
      // Ensure we do not pass too much data
      if (subject && subject.subjectReviews && subject.subjectReviews.length > 0) {
        subject.subjectReviews.forEach(i => {
          if (i.review && i.review.reviewedById > 0) {
            i.review.reviewedBy = null;
          }
        });
      }
      if (assessment) {
        // ...link to assessment, and link assessment to subject

        assessment.reviewId = response.id;
        subject.subjectAssessments = subject.subjectAssessments || [];
        let subjectAssessment: SubjectAssessment;
        if (assessment.id) {
          subjectAssessment = subject.subjectAssessments.find((i) => i.assessmentId === assessment.id);
          subjectAssessment.assessment = assessment;
        }
        if (!subjectAssessment) {
          subject.subjectAssessments.push(new SubjectAssessment({ assessment }));
        }
      }

      // save the subject along with the new review (and assessment if specified)
      return this.subjectService.saveAsObservable(subject, (assessment) ? 'Assessment successfully completed' : 'Review successfully completed').pipe(map((s: Subject) => {
        const subjectReview = s.subjectReviews.find(i => i.reviewId === response.id);
        if (subjectReview) {
          subjectReview.review = response;
          subjectReview.review.reviewedBy = reviewedBy;
        }
        const sub = s.subjectAssessments.filter((i) => i.assessment.reviewId === response.id);
        sub.forEach(i => {
          i.assessment.review.reviewedBy = reviewedBy;
        });

        // make sure any datafield changes are updated
        this.eventService.updateSubjectsDueReviewStats();
        
        this.subjectTypeService.refresh();
        return s;
      }));
    }));
  }

  saveAsObservable(entity: Review, successMessage?: string): Observable<Review> {
    return (entity.id) ? this.put(entity, successMessage) : this.post(entity, successMessage);
  }
}
